import { useMemo } from 'react';
import {
  ALL_TIME_PERIOD,
  describeFinancialPeriod,
  toDateInput,
  useFinancialPeriod,
} from '../../context/FinancialPeriodContext';
import { BILLING_HISTORY_DAYS, useBillingHistoryAccess } from '../../utils/billingAccess';
import { DatePicker } from './DatePicker';

/**
 * Date-range control shared by the Billing overview and the Top Sheet.
 *
 * Both pages are views of the same consolidated report, so they share one
 * period (see {@link useFinancialPeriod}). Changing it here changes it
 * everywhere, and because the pages then issue the same request there is a
 * single React Query cache entry behind both of them.
 *
 * Only accounts allowed the full billing history — `admin`, `super_admin`, or a
 * custom role holding `billing:history:full` — may choose an arbitrary range:
 * the From/To pickers and the All Time action are rendered for them alone.
 * Every other role is pinned to the most recent {@link BILLING_HISTORY_DAYS}
 * days and gets just the presets that fit inside that window. The API clamps the
 * same range server-side, so hiding the controls is a usability decision, not the
 * security boundary.
 */
export function PeriodPicker({ className = '' }: { className?: string }) {
  const { period, setPeriod } = useFinancialPeriod();
  const { unrestricted } = useBillingHistoryAccess();

  const quickRanges = useMemo(() => {
    const today = new Date();
    const ranges = [
      { label: 'Today', from: toDateInput(today), to: toDateInput(today) },
      { label: '7 Days', from: toDateInput(new Date(today.getTime() - 6 * 86400000)), to: toDateInput(today) },
      { label: '30 Days', from: toDateInput(new Date(today.getTime() - 29 * 86400000)), to: toDateInput(today) },
      {
        label: 'This Month',
        from: toDateInput(new Date(today.getFullYear(), today.getMonth(), 1)),
        to: toDateInput(today),
      },
      { label: 'This Year', from: toDateInput(new Date(today.getFullYear(), 0, 1)), to: toDateInput(today) },
      { label: 'All Time', from: ALL_TIME_PERIOD.from, to: ALL_TIME_PERIOD.to },
    ];
    // Restricted roles may only reach back seven days, so longer presets and
    // "All Time" are never offered to them.
    return unrestricted ? ranges : ranges.slice(0, 2);
  }, [unrestricted]);

  const setFrom = (from: string) => setPeriod({ ...period, from });
  const setTo = (to: string) => setPeriod({ ...period, to });

  return (
    <div className={`bg-surface rounded-xl border border-border p-4 space-y-3 ${className}`}>
      {unrestricted ? (
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-44">
            <label className="block text-xs font-medium text-text-muted mb-1 uppercase">From</label>
            <DatePicker value={period.from} onChange={setFrom} />
          </div>
          <div className="w-44">
            <label className="block text-xs font-medium text-text-muted mb-1 uppercase">To</label>
            <DatePicker value={period.to} onChange={setTo} />
          </div>
          <button
            type="button"
            onClick={() => setPeriod(ALL_TIME_PERIOD)}
            className="px-3 py-2 text-sm text-text-muted hover:text-text border border-border rounded-lg transition-colors"
          >
            All Time
          </button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="text-xs font-medium text-text-muted uppercase">Reporting period</p>
            <p className="text-sm font-medium text-text">{describeFinancialPeriod(period)}</p>
          </div>
          <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-primary/10 text-primary">
            Last {BILLING_HISTORY_DAYS} days
          </span>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {quickRanges.map((range) => {
          const active = period.from === range.from && period.to === range.to;
          return (
            <button
              key={range.label}
              type="button"
              onClick={() => setPeriod({ from: range.from, to: range.to })}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                active
                  ? 'bg-primary text-white border-primary'
                  : 'bg-background text-text-muted border-border hover:text-text'
              }`}
            >
              {range.label}
            </button>
          );
        })}
      </div>
      {!unrestricted && (
        <p className="text-xs text-text-muted">
          Showing at most the last {BILLING_HISTORY_DAYS} days. Ask an admin for an older report.
        </p>
      )}
    </div>
  );
}
